import React from "react";
import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import { CONTACT } from "@/data/siteContent";

export default function Contact({ onBookConsultation }) {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-navy text-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" aria-hidden>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gold rounded-full blur-[200px]" />
      </div>
      <div className="relative container-prose text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="eyebrow mb-5"
        >
          — Reach Out
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl mx-auto text-balance"
        >
          Your child's journey starts with <span className="italic text-gold">one conversation.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-6 text-white/75 text-lg max-w-2xl mx-auto"
        >
          Book a free 30-minute consultation with our advisors. No commitment. Just clarity.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10"
        >
          <button
            data-testid="contact-book-consultation"
            onClick={onBookConsultation}
            className="inline-flex items-center gap-3 bg-gold text-navy font-semibold px-9 py-5 text-sm md:text-base tracking-wide uppercase rounded-sm hover:bg-gold-hover transition-all shadow-[0_20px_50px_-18px_rgba(245,197,24,0.7)] hover:shadow-[0_24px_60px_-18px_rgba(245,197,24,0.9)] hover:-translate-y-0.5"
          >
            Book a Free Consultation
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-16 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-14 text-white/80"
        >
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="contact-email"
            className="group inline-flex items-center gap-3 hover:text-gold transition-colors"
          >
            <span className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:border-gold">
              <Mail size={16} />
            </span>
            <span className="text-sm md:text-base">{CONTACT.email}</span>
          </a>
          <div className="hidden md:block h-6 w-px bg-white/15" />
          <div className="flex items-center gap-5">
            {CONTACT.phones.map((p) => (
              <a
                key={p}
                href={`tel:${p.replace(/\s/g, "")}`}
                data-testid={`contact-phone-${p.replace(/\s|\+/g, "")}`}
                className="group inline-flex items-center gap-3 hover:text-gold transition-colors"
              >
                <span className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:border-gold">
                  <Phone size={16} />
                </span>
                <span className="text-sm md:text-base">{p}</span>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
